/**
 * Chart of Accounts 分类映射（Taiwan GAAP 7 位码）
 *
 * 唯一真相源：`AX 會計科目Tài khoản kế toán.xlsx`
 * 基于 Excel 里 code(4 位前缀) + name + type 推导，不使用 AX 导出里的 Main-account-category 列
 * （那列导出时错位了：1247 行只有 181 行有值，且 181 个值全不重复）
 *
 * 映射 4 位前缀 → category；一个 category 可被多个前缀共享（例如 PPE_COST 覆盖 1612-1619）。
 */
export const COA_CATEGORY = {
    CASH: 'CASH',
    BANK: 'BANK',
    CASH_FX_RESERVE: 'CASH_FX_RESERVE',
    NOTES_RECV: 'NOTES_RECV',
    AR: 'AR',
    OTHER_RECV: 'OTHER_RECV',
    INV_RAW: 'INV_RAW',
    INV_WIP: 'INV_WIP',
    INV_FG: 'INV_FG',
    INV_MERCH: 'INV_MERCH',
    INV_TRANSIT: 'INV_TRANSIT',
    INV_RESERVE: 'INV_RESERVE',
    PREPAID_INV: 'PREPAID_INV',
    PREPAID_EXP: 'PREPAID_EXP',
    SUPPLIES: 'SUPPLIES',
    DEFERRED_EXP_CUR: 'DEFERRED_EXP_CUR',
    INPUT_TAX: 'INPUT_TAX',
    SUSPENSE_DR: 'SUSPENSE_DR',
    DEFERRED_TAX_ASSET_CUR: 'DEFERRED_TAX_ASSET_CUR',
    DEFERRED_EXP: 'DEFERRED_EXP',
    EQUITY_METHOD_INVEST: 'EQUITY_METHOD_INVEST',
    FVTOCI_INVEST: 'FVTOCI_INVEST',
    PPE_COST: 'PPE_COST',
    ACCUM_DEP: 'ACCUM_DEP',
    GOODWILL: 'GOODWILL',
    SOFTWARE: 'SOFTWARE',
    PATENT: 'PATENT',
    DEPOSIT_OUT: 'DEPOSIT_OUT',
    DEFERRED_EXP_NONCUR: 'DEFERRED_EXP_NONCUR',
    ROU_ASSET: 'ROU_ASSET',
    ROU_ACCUM_DEP: 'ROU_ACCUM_DEP',
    DEFERRED_TAX_ASSET_NONCUR: 'DEFERRED_TAX_ASSET_NONCUR',
    OTHER_ASSET: 'OTHER_ASSET',
    ST_LOAN: 'ST_LOAN',
    ST_LOAN_OTHER: 'ST_LOAN_OTHER',
    LTD_CURRENT: 'LTD_CURRENT',
    NOTES_PAY: 'NOTES_PAY',
    AP: 'AP',
    ACCRUED_EXP: 'ACCRUED_EXP',
    OTHER_AP: 'OTHER_AP',
    AP_CAPEX: 'AP_CAPEX',
    TAX_PAY: 'TAX_PAY',
    UNEARNED_REV: 'UNEARNED_REV',
    INTERCO_PAY: 'INTERCO_PAY',
    LEASE_LIAB_CUR: 'LEASE_LIAB_CUR',
    OUTPUT_TAX: 'OUTPUT_TAX',
    WITHHOLDING: 'WITHHOLDING',
    SUSPENSE_CR: 'SUSPENSE_CR',
    LT_LOAN: 'LT_LOAN',
    DEPOSIT_IN: 'DEPOSIT_IN',
    DEFERRED_TAX_LIAB: 'DEFERRED_TAX_LIAB',
    OTHER_LIAB: 'OTHER_LIAB',
    LEASE_LIAB_NONCUR: 'LEASE_LIAB_NONCUR',
    PROVISIONS: 'PROVISIONS',
    AR_ALLOWANCE: 'AR_ALLOWANCE',
    PPE_IMPAIRMENT: 'PPE_IMPAIRMENT',
    COMMON_STOCK: 'COMMON_STOCK',
    APIC: 'APIC',
    LEGAL_RESERVE: 'LEGAL_RESERVE',
    SPECIAL_RESERVE: 'SPECIAL_RESERVE',
    RETAINED_EARNINGS: 'RETAINED_EARNINGS',
    OCI: 'OCI',
    TREASURY: 'TREASURY',
    NCI: 'NCI',
    REVENUE_SALES: 'REVENUE_SALES',
    REVENUE_PROCESSING: 'REVENUE_PROCESSING',
    REVENUE_SERVICE: 'REVENUE_SERVICE',
    REVENUE_OTHER: 'REVENUE_OTHER',
    REVENUE_COMMISSION: 'REVENUE_COMMISSION',
    REVENUE_INTERCO: 'REVENUE_INTERCO',
    SALES_CONTRA: 'SALES_CONTRA',
    COGS: 'COGS',
    INV_VARIANCE: 'INV_VARIANCE',
    COGS_OTHER: 'COGS_OTHER',
    COGS_ADJUST: 'COGS_ADJUST',
    PROCESSING_COST: 'PROCESSING_COST',
    COST_VARIANCE: 'COST_VARIANCE',
    SELLING_EXP: 'SELLING_EXP',
    RND_EXP: 'RND_EXP',
    ADMIN_EXP: 'ADMIN_EXP',
    NON_OP_INCOME: 'NON_OP_INCOME',
    NON_OP_EXPENSE: 'NON_OP_EXPENSE',
    INCOME_TAX: 'INCOME_TAX',
    NCI_PNL: 'NCI_PNL',
    OTHER_PNL: 'OTHER_PNL',
};
/** 4 位前缀 → category（覆盖 Excel 里出现过的所有段） */
export const PREFIX_TO_CATEGORY = {
    // 资产 1xxx
    '1101': COA_CATEGORY.CASH, '1111': COA_CATEGORY.BANK, '1119': COA_CATEGORY.CASH_FX_RESERVE,
    '1131': COA_CATEGORY.NOTES_RECV, '1132': COA_CATEGORY.AR, '1139': COA_CATEGORY.OTHER_RECV,
    '1141': COA_CATEGORY.INV_RAW, '1142': COA_CATEGORY.INV_WIP, '1143': COA_CATEGORY.INV_FG,
    '1144': COA_CATEGORY.INV_MERCH, '1148': COA_CATEGORY.INV_TRANSIT, '1149': COA_CATEGORY.INV_RESERVE,
    '1151': COA_CATEGORY.PREPAID_INV, '1152': COA_CATEGORY.PREPAID_EXP, '1153': COA_CATEGORY.SUPPLIES,
    '1155': COA_CATEGORY.DEFERRED_EXP_CUR, '1161': COA_CATEGORY.INPUT_TAX, '1162': COA_CATEGORY.SUSPENSE_DR,
    '1164': COA_CATEGORY.DEFERRED_TAX_ASSET_CUR, '1165': COA_CATEGORY.DEFERRED_EXP,
    '1211': COA_CATEGORY.EQUITY_METHOD_INVEST, '1517': COA_CATEGORY.FVTOCI_INVEST,
    '1612': COA_CATEGORY.PPE_COST, '1613': COA_CATEGORY.PPE_COST, '1614': COA_CATEGORY.PPE_COST,
    '1615': COA_CATEGORY.PPE_COST, '1616': COA_CATEGORY.PPE_COST, '1617': COA_CATEGORY.PPE_COST,
    '1618': COA_CATEGORY.PPE_COST, '1619': COA_CATEGORY.PPE_COST,
    '1682': COA_CATEGORY.ACCUM_DEP, '1683': COA_CATEGORY.ACCUM_DEP, '1684': COA_CATEGORY.ACCUM_DEP,
    '1685': COA_CATEGORY.ACCUM_DEP, '1686': COA_CATEGORY.ACCUM_DEP, '1687': COA_CATEGORY.ACCUM_DEP,
    '1688': COA_CATEGORY.ACCUM_DEP,
    '1810': COA_CATEGORY.GOODWILL, '1820': COA_CATEGORY.SOFTWARE, '1830': COA_CATEGORY.PATENT,
    '1910': COA_CATEGORY.DEPOSIT_OUT, '1920': COA_CATEGORY.DEFERRED_EXP_NONCUR,
    '1931': COA_CATEGORY.OTHER_ASSET, '1979': COA_CATEGORY.DEFERRED_EXP_NONCUR,
    '1981': COA_CATEGORY.ROU_ASSET, '1982': COA_CATEGORY.ROU_ACCUM_DEP,
    '1994': COA_CATEGORY.OTHER_ASSET, '1995': COA_CATEGORY.DEFERRED_TAX_ASSET_NONCUR,
    '1997': COA_CATEGORY.OTHER_ASSET, '1998': COA_CATEGORY.OTHER_ASSET,
    // 负债 2xxx
    '2111': COA_CATEGORY.ST_LOAN, '2115': COA_CATEGORY.ST_LOAN_OTHER, '2119': COA_CATEGORY.ST_LOAN_OTHER,
    '2120': COA_CATEGORY.LTD_CURRENT, '2121': COA_CATEGORY.NOTES_PAY, '2122': COA_CATEGORY.AP,
    '2131': COA_CATEGORY.ACCRUED_EXP, '2132': COA_CATEGORY.OTHER_AP, '2133': COA_CATEGORY.AP_CAPEX,
    '2134': COA_CATEGORY.OTHER_AP, '2135': COA_CATEGORY.TAX_PAY, '2136': COA_CATEGORY.UNEARNED_REV,
    '2140': COA_CATEGORY.INTERCO_PAY, '2164': COA_CATEGORY.LEASE_LIAB_CUR,
    '2192': COA_CATEGORY.OUTPUT_TAX, '2193': COA_CATEGORY.WITHHOLDING, '2194': COA_CATEGORY.SUSPENSE_CR,
    '2311': COA_CATEGORY.LT_LOAN, '2312': COA_CATEGORY.LT_LOAN, '2313': COA_CATEGORY.LT_LOAN,
    '2910': COA_CATEGORY.DEPOSIT_IN, '2991': COA_CATEGORY.OTHER_LIAB, '2992': COA_CATEGORY.OTHER_LIAB,
    '2995': COA_CATEGORY.DEFERRED_TAX_LIAB,
    // 权益 3xxx
    '3110': COA_CATEGORY.COMMON_STOCK, '3200': COA_CATEGORY.COMMON_STOCK,
    '3410': COA_CATEGORY.APIC, '3420': COA_CATEGORY.APIC, '3430': COA_CATEGORY.APIC,
    '3510': COA_CATEGORY.LEGAL_RESERVE, '3520': COA_CATEGORY.SPECIAL_RESERVE,
    '3530': COA_CATEGORY.RETAINED_EARNINGS, '3600': COA_CATEGORY.OCI,
    '3700': COA_CATEGORY.TREASURY, '3900': COA_CATEGORY.NCI,
    // 收入 4xxx
    '4111': COA_CATEGORY.REVENUE_SALES, '4112': COA_CATEGORY.REVENUE_SALES, '4113': COA_CATEGORY.REVENUE_SALES,
    '4201': COA_CATEGORY.REVENUE_PROCESSING, '4202': COA_CATEGORY.REVENUE_PROCESSING,
    '4301': COA_CATEGORY.REVENUE_SERVICE, '4501': COA_CATEGORY.REVENUE_OTHER,
    '4701': COA_CATEGORY.REVENUE_COMMISSION, '4800': COA_CATEGORY.REVENUE_INTERCO,
    '4911': COA_CATEGORY.SALES_CONTRA, '4921': COA_CATEGORY.SALES_CONTRA,
    '4931': COA_CATEGORY.SALES_CONTRA, '4932': COA_CATEGORY.SALES_CONTRA,
    // 成本 5xxx
    '5111': COA_CATEGORY.COGS, '5112': COA_CATEGORY.COGS, '5113': COA_CATEGORY.COGS,
    '5121': COA_CATEGORY.COGS, '5122': COA_CATEGORY.COGS, '5123': COA_CATEGORY.COGS,
    '5124': COA_CATEGORY.COGS, '5125': COA_CATEGORY.COGS, '5129': COA_CATEGORY.COGS, '5131': COA_CATEGORY.COGS,
    '5141': COA_CATEGORY.INV_VARIANCE, '5142': COA_CATEGORY.INV_VARIANCE,
    '5151': COA_CATEGORY.COGS_OTHER, '5152': COA_CATEGORY.COGS_OTHER, '5153': COA_CATEGORY.COGS_OTHER,
    '5170': COA_CATEGORY.COGS_ADJUST, '5180': COA_CATEGORY.COGS_ADJUST, '5190': COA_CATEGORY.COGS_ADJUST,
    '5201': COA_CATEGORY.PROCESSING_COST, '5202': COA_CATEGORY.PROCESSING_COST,
    '5900': COA_CATEGORY.COST_VARIANCE, '5910': COA_CATEGORY.COST_VARIANCE,
    // 营业费用 6xxx
    '6101': COA_CATEGORY.SELLING_EXP, '6102': COA_CATEGORY.SELLING_EXP,
    '6201': COA_CATEGORY.RND_EXP, '6202': COA_CATEGORY.RND_EXP, '6211': COA_CATEGORY.RND_EXP,
    '6301': COA_CATEGORY.ADMIN_EXP,
    // 营业外 7xxx
    '7101': COA_CATEGORY.NON_OP_INCOME, '7102': COA_CATEGORY.NON_OP_INCOME, '7103': COA_CATEGORY.NON_OP_INCOME,
    '7104': COA_CATEGORY.NON_OP_INCOME, '7105': COA_CATEGORY.NON_OP_INCOME, '7106': COA_CATEGORY.NON_OP_INCOME,
    '7107': COA_CATEGORY.NON_OP_INCOME, '7108': COA_CATEGORY.NON_OP_INCOME,
    '7201': COA_CATEGORY.NON_OP_EXPENSE, '7202': COA_CATEGORY.NON_OP_EXPENSE, '7203': COA_CATEGORY.NON_OP_EXPENSE,
    '7204': COA_CATEGORY.NON_OP_EXPENSE, '7205': COA_CATEGORY.NON_OP_EXPENSE, '7206': COA_CATEGORY.NON_OP_EXPENSE,
    '7207': COA_CATEGORY.NON_OP_EXPENSE, '7209': COA_CATEGORY.NON_OP_EXPENSE,
    // 所得税/其他 8xxx
    '8201': COA_CATEGORY.INCOME_TAX, '8299': COA_CATEGORY.INCOME_TAX, '8301': COA_CATEGORY.INCOME_TAX,
    '8401': COA_CATEGORY.NCI_PNL, '8501': COA_CATEGORY.OTHER_PNL,
    // PSP 越南本地补充（CAS/VAS 4 位码）
    '1125': COA_CATEGORY.AR_ALLOWANCE, '1531': COA_CATEGORY.DEFERRED_TAX_ASSET_NONCUR,
    '1621': COA_CATEGORY.PPE_IMPAIRMENT,
    '1801': COA_CATEGORY.ROU_ASSET, '1802': COA_CATEGORY.ROU_ACCUM_DEP,
    '2251': COA_CATEGORY.LEASE_LIAB_CUR, '2601': COA_CATEGORY.LEASE_LIAB_NONCUR,
    '2701': COA_CATEGORY.DEFERRED_TAX_LIAB, '2801': COA_CATEGORY.PROVISIONS,
    '3201': COA_CATEGORY.OCI, '3202': COA_CATEGORY.OCI,
    '6601': COA_CATEGORY.ADMIN_EXP, '6701': COA_CATEGORY.NON_OP_EXPENSE,
    '6801': COA_CATEGORY.INCOME_TAX,
};
/** 根据科目码取 category。查 4 位前缀；查不到返回 null */
export function getCategoryForCode(code) {
    if (!code || code.length < 4)
        return null;
    return PREFIX_TO_CATEGORY[code.substring(0, 4)] ?? null;
}
/**
 * Account Config 代表科目前缀：每个业务位指向一个代表 category 的 4 位前缀
 * 落到 DB 里的 `configKey → accountCode` 最终取该前缀下的第一条真实 GLAccount.id
 */
export const ACCOUNT_CONFIG_PREFIX = {
    AR: '1132', AP: '2122',
    REVENUE: '4111', COGS: '5111',
    INVENTORY: '1141',
    CASH: '1101', BANK: '1111',
    INPUT_TAX: '1161', OUTPUT_TAX: '2192',
    FIXED_ASSET: '1613', ACCUM_DEPRECIATION: '1683',
    DEPRECIATION_EXPENSE: '6301', DISPOSAL_GAIN: '7103', DISPOSAL_LOSS: '7204',
    PRODUCTION_COST: '5201', ADMIN_EXPENSE: '6301', FINANCE_EXPENSE: '7201',
};
/** 现金 / 银行科目的前缀（供过滤用，代替散落各处的 '1001' / '1002' 硬编码） */
export const CASH_CODE_PREFIX = '1101';
export const BANK_CODE_PREFIXES = ['1111', '1112'];
